/**
 * Email Builder Component
 * Professional drag-drop email editor using Unlayer (react-email-editor)
 * Replicates Sendy's email builder functionality
 *
 * Created: January 4, 2026
 */

import React, { useRef, useState, useEffect } from "react";
import EmailEditor from "react-email-editor";
import { Row, Col, Button, Form, Tabs, Tab, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import TestEmailModal from "./TestEmailModal";
import ImageLibrary from "./ImageLibrary";

// Default blank design for new templates
const BLANK_DESIGN = {
  counters: { u_column: 1, u_row: 1, u_content_text: 1 },
  body: {
    id: "root",
    rows: [
      {
        id: "row-1",
        cells: [1],
        columns: [
          {
            id: "col-1",
            contents: [
              {
                id: "content-1",
                type: "text",
                values: {
                  containerPadding: "10px",
                  anchor: "",
                  textAlign: "left",
                  lineHeight: "140%",
                  linkStyle: {
                    inherit: true,
                    linkColor: "#0000ee",
                    linkHoverColor: "#0000ee",
                    linkUnderline: true,
                    linkHoverUnderline: true,
                  },
                  hideDesktop: false,
                  displayCondition: null,
                  _meta: { htmlID: "u_content_text_1", htmlClassNames: "u_content_text" },
                  selectable: true,
                  draggable: true,
                  duplicatable: true,
                  deletable: true,
                  hideable: true,
                  text: "<p style=\"font-size: 14px; line-height: 140%;\">Start building your email here...</p>",
                },
              },
            ],
            values: {
              backgroundColor: "",
              padding: "0px",
              border: {},
              borderRadius: "0px",
              _meta: { htmlID: "u_column_1", htmlClassNames: "u_column" },
            },
          },
        ],
        values: {
          displayCondition: null,
          columns: false,
          backgroundColor: "",
          columnsBackgroundColor: "",
          backgroundImage: { url: "", fullWidth: true, repeat: false, center: true, cover: false },
          padding: "0px",
          anchor: "",
          hideDesktop: false,
          _meta: { htmlID: "u_row_1", htmlClassNames: "u_row" },
          selectable: true,
          draggable: true,
          duplicatable: true,
          deletable: true,
          hideable: true,
        },
      },
    ],
    values: {
      popupPosition: "center",
      popupWidth: "600px",
      popupHeight: "auto",
      borderRadius: "10px",
      contentAlign: "center",
      contentVerticalAlign: "center",
      contentWidth: "600px",
      fontFamily: { label: "Arial", value: "arial,helvetica,sans-serif" },
      textColor: "#000000",
      popupBackgroundColor: "#FFFFFF",
      popupBackgroundImage: { url: "", fullWidth: true, repeat: false, center: true, cover: true },
      popupOverlay_backgroundColor: "rgba(0, 0, 0, 0.1)",
      popupCloseButton_position: "top-right",
      popupCloseButton_backgroundColor: "#DDDDDD",
      popupCloseButton_iconColor: "#000000",
      popupCloseButton_borderRadius: "0px",
      popupCloseButton_margin: "0px",
      popupCloseButton_action: { name: "close_popup", attrs: { onClick: "document.querySelector('.u-popup-container').style.display = 'none';" } },
      backgroundColor: "#ffffff",
      backgroundImage: { url: "", fullWidth: true, repeat: false, center: true, cover: false },
      preheaderText: "",
      linkStyle: { body: true, linkColor: "#D2000B", linkHoverColor: "#B61624", linkUnderline: true, linkHoverUnderline: true },
      _meta: { htmlID: "u_body", htmlClassNames: "u_body" },
    },
  },
};

// WTF Gyms branded template
const WTF_BRANDED_DESIGN = {
  counters: { u_column: 3, u_row: 3, u_content_image: 1, u_content_text: 3, u_content_button: 1 },
  body: {
    id: "root",
    rows: [
      // Header row
      {
        id: "header-row",
        cells: [1],
        columns: [
          {
            id: "header-col",
            contents: [
              {
                id: "logo",
                type: "image",
                values: {
                  containerPadding: "20px",
                  anchor: "",
                  src: { url: "https://wtfgyms.com/logo-white.png", width: 150, height: 50 },
                  textAlign: "center",
                  altText: "WTF Gyms",
                  action: { name: "web", values: { href: "https://wtfgyms.com", target: "_blank" } },
                  hideDesktop: false,
                  _meta: { htmlID: "u_content_image_1", htmlClassNames: "u_content_image" },
                },
              },
            ],
            values: { backgroundColor: "#D2000B", padding: "10px" },
          },
        ],
        values: { backgroundColor: "#D2000B", padding: "0px" },
      },
      // Content row
      {
        id: "content-row",
        cells: [1],
        columns: [
          {
            id: "content-col",
            contents: [
              {
                id: "greeting",
                type: "text",
                values: {
                  containerPadding: "30px 20px 10px 20px",
                  text: "<h2 style=\"font-size: 24px; color: #333;\">Hi [Name,fallback=there]!</h2>",
                  textAlign: "left",
                  lineHeight: "140%",
                },
              },
              {
                id: "body-text",
                type: "text",
                values: {
                  containerPadding: "10px 20px",
                  text: "<p style=\"font-size: 16px; color: #555; line-height: 1.6;\">Your email content goes here. We're excited to share this with you.</p>",
                  textAlign: "left",
                  lineHeight: "160%",
                },
              },
              {
                id: "cta-button",
                type: "button",
                values: {
                  containerPadding: "20px",
                  anchor: "",
                  href: { name: "web", values: { href: "https://wtfgyms.com", target: "_blank" } },
                  buttonColors: { color: "#FFFFFF", backgroundColor: "#D2000B", hoverColor: "#FFFFFF", hoverBackgroundColor: "#B61624" },
                  size: { autoWidth: true, width: "100%" },
                  textAlign: "center",
                  lineHeight: "120%",
                  padding: "15px 30px",
                  border: {},
                  borderRadius: "5px",
                  text: "<span style=\"font-size: 16px; font-weight: bold;\">Learn More</span>",
                  _meta: { htmlID: "u_content_button_1", htmlClassNames: "u_content_button" },
                },
              },
            ],
            values: { backgroundColor: "#ffffff", padding: "0px" },
          },
        ],
        values: { backgroundColor: "#ffffff", padding: "0px" },
      },
      // Footer row
      {
        id: "footer-row",
        cells: [1],
        columns: [
          {
            id: "footer-col",
            contents: [
              {
                id: "footer-text",
                type: "text",
                values: {
                  containerPadding: "20px",
                  text: `<p style="font-size: 12px; color: #666; text-align: center;">
                    &copy; 2026 WTF Gyms. All rights reserved.<br/>
                    <a href="[Unsubscribe_Link]" style="color: #D2000B;">Unsubscribe</a> |
                    <a href="[Web_Version_Link]" style="color: #D2000B;">View in Browser</a>
                  </p>`,
                  textAlign: "center",
                  lineHeight: "140%",
                },
              },
            ],
            values: { backgroundColor: "#f5f5f5", padding: "10px" },
          },
        ],
        values: { backgroundColor: "#f5f5f5", padding: "0px" },
      },
    ],
    values: {
      contentWidth: "600px",
      contentAlign: "center",
      fontFamily: { label: "Arial", value: "arial,helvetica,sans-serif" },
      textColor: "#333333",
      backgroundColor: "#f0f0f0",
      linkStyle: { body: true, linkColor: "#D2000B", linkHoverColor: "#B61624", linkUnderline: true },
      preheaderText: "",
    },
  },
};

// Pre-built templates
const TEMPLATE_PRESETS = {
  blank: { name: "Blank", design: BLANK_DESIGN },
  wtf_branded: { name: "WTF Branded", design: WTF_BRANDED_DESIGN },
  welcome: { name: "Welcome Email", design: WTF_BRANDED_DESIGN },
  promotional: { name: "Promotional Offer", design: WTF_BRANDED_DESIGN },
  newsletter: { name: "Newsletter", design: WTF_BRANDED_DESIGN },
  renewal: { name: "Renewal Reminder", design: WTF_BRANDED_DESIGN },
};

// Personalization merge tags
const MERGE_TAGS = [
  { name: "Name", value: "[Name]" },
  { name: "Name with Fallback", value: "[Name,fallback=Hey]" },
  { name: "Email", value: "[Email]" },
  { name: "Phone", value: "[Phone]" },
  { name: "Gym Name", value: "[Gym_Name]" },
  { name: "Current Date", value: "[Date]" },
  { name: "Unsubscribe Link", value: "[Unsubscribe_Link]" },
  { name: "Web Version", value: "[Web_Version_Link]" },
];

// Special links for email
const SPECIAL_LINKS = [
  { name: "Unsubscribe", href: "[Unsubscribe_Link]", target: "_blank" },
  { name: "View in Browser", href: "[Web_Version_Link]", target: "_blank" },
  { name: "WTF Gyms Website", href: "https://wtfgyms.com", target: "_blank" },
];

const EmailBuilder = ({
  initialDesign,
  initialHtml,
  onSave,
  onExportHtml,
  templateName = "",
  templateSubject = "",
  readOnly = false,
  token = "",
  userEmail = "",
  fromName = "WTF Gyms",
  fromEmail = "noreply@wtfgyms.com",
}) => {
  const emailEditorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");
  const [activeTab, setActiveTab] = useState("editor");
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailData, setTestEmailData] = useState(null);
  const [showImageLibrary, setShowImageLibrary] = useState(false);

  // Editor configuration options
  const editorOptions = {
    displayMode: "email",
    projectId: 1, // Free tier
    locale: "en-US",
    appearance: {
      theme: "light",
      panels: {
        tools: {
          dock: "left",
        },
      },
    },
    tools: {
      // Enable all tools
      text: { enabled: true },
      image: { enabled: true },
      button: { enabled: true },
      divider: { enabled: true },
      spacer: { enabled: true },
      social: { enabled: true },
      video: { enabled: true },
      menu: { enabled: true },
      html: { enabled: true },
      heading: { enabled: true },
    },
    features: {
      textEditor: {
        spellChecker: true,
        cleanPaste: true,
        tables: true,
      },
      stockImages: {
        enabled: true,
        safeSearch: true,
      },
    },
    mergeTags: MERGE_TAGS.map((tag) => ({
      name: tag.name,
      value: tag.value,
    })),
    specialLinks: SPECIAL_LINKS,
  };

  const onEditorLoad = () => {
    console.log("Email editor loaded");
  };

  const onEditorReady = () => {
    console.log("Email editor ready");
    setEditorReady(true);

    // Register custom image upload callback
    emailEditorRef.current?.editor?.registerCallback('image', (file, done) => {
      // Open media library instead of default upload
      setShowImageLibrary(true);

      // Store the done callback for later use
      window._unlayerImageCallback = done;
    });

    // Load initial design if provided
    if (initialDesign) {
      try {
        const design = typeof initialDesign === "string" ? JSON.parse(initialDesign) : initialDesign;
        emailEditorRef.current?.editor?.loadDesign(design);
      } catch (e) {
        console.error("Failed to load initial design:", e);
        emailEditorRef.current?.editor?.loadDesign(BLANK_DESIGN);
      }
    } else if (initialHtml) {
      // If only HTML is provided, load it
      // Note: This is limited - Unlayer prefers JSON designs
      emailEditorRef.current?.editor?.loadDesign(BLANK_DESIGN);
    } else {
      // Load WTF branded template by default
      emailEditorRef.current?.editor?.loadDesign(WTF_BRANDED_DESIGN);
    }
  };

  const loadTemplate = (presetKey) => {
    if (emailEditorRef.current?.editor && TEMPLATE_PRESETS[presetKey]) {
      emailEditorRef.current.editor.loadDesign(TEMPLATE_PRESETS[presetKey].design);
      toast.success(`Loaded "${TEMPLATE_PRESETS[presetKey].name}" template`);
    }
  };

  const exportHtml = (callback) => {
    emailEditorRef.current?.editor?.exportHtml((data) => {
      const { design, html } = data;
      if (callback) {
        callback({ design: JSON.stringify(design), html });
      }
    });
  };

  const handleSave = () => {
    exportHtml((data) => {
      if (onSave) {
        onSave(data);
      }
    });
  };

  const handleExportHtml = () => {
    exportHtml((data) => {
      setHtmlSource(data.html);
      setShowHtmlSource(true);
      if (onExportHtml) {
        onExportHtml(data.html);
      }
    });
  };

  const handlePreview = (mode) => {
    setPreviewMode(mode);
    if (emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.setDisplayMode(mode === "mobile" ? "mobile" : "email");
    }
  };

  const insertMergeTag = (tag) => {
    // Copy to clipboard for user to paste
    navigator.clipboard.writeText(tag.value);
    toast.info(`"${tag.value}" copied! Paste it in your text.`);
  };

  const handleSendTest = () => {
    if (!token) {
      toast.error("Authentication required. Please log in.");
      return;
    }

    // Export current HTML from editor
    exportHtml((data) => {
      setTestEmailData({
        subject: templateSubject,
        html_body: data.html,
        from_name: fromName,
        from_email: fromEmail,
      });
      setShowTestEmailModal(true);
    });
  };

  // Handle image selection from library
  const handleSelectImageFromLibrary = (imageUrl) => {
    // Call the Unlayer callback if it exists
    if (window._unlayerImageCallback) {
      window._unlayerImageCallback({ url: imageUrl });
      window._unlayerImageCallback = null;
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(imageUrl);
      toast.success("Image URL copied! Paste it in your email.");
    }
  };

  return (
    <div className="email-builder-container" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <div className="email-builder-toolbar bg-light border-bottom p-2 mb-2">
        <Row className="align-items-center">
          <Col md={4}>
            <div className="d-flex gap-2">
              <Form.Select
                size="sm"
                onChange={(e) => loadTemplate(e.target.value)}
                defaultValue=""
                style={{ width: "auto" }}
              >
                <option value="" disabled>
                  Load Template...
                </option>
                {Object.entries(TEMPLATE_PRESETS).map(([key, preset]) => (
                  <option key={key} value={key}>
                    {preset.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>

          <Col md={4} className="text-center">
            <div className="btn-group btn-group-sm">
              <Button
                variant={previewMode === "desktop" ? "primary" : "outline-secondary"}
                onClick={() => handlePreview("desktop")}
                size="sm"
              >
                Desktop
              </Button>
              <Button
                variant={previewMode === "mobile" ? "primary" : "outline-secondary"}
                onClick={() => handlePreview("mobile")}
                size="sm"
              >
                Mobile
              </Button>
            </div>
          </Col>

          <Col md={4} className="text-end">
            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowImageLibrary(true)}
                title="Open Media Library"
              >
                <i className="bi bi-images me-1" />
                Media
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleExportHtml}>
                View HTML
              </Button>
              {token && (
                <Button variant="outline-primary" size="sm" onClick={handleSendTest}>
                  Send Test
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
              >
                Save Template
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Merge Tags Bar */}
      <div className="merge-tags-bar bg-white border-bottom p-2">
        <small className="text-muted me-2">Personalization:</small>
        {MERGE_TAGS.slice(0, 5).map((tag) => (
          <Badge
            key={tag.value}
            bg="light"
            text="dark"
            className="me-1 cursor-pointer"
            style={{ cursor: "pointer", border: "1px solid #ddd" }}
            onClick={() => insertMergeTag(tag)}
          >
            {tag.name}
          </Badge>
        ))}
        <Badge
          bg="secondary"
          className="ms-1"
          style={{ cursor: "pointer" }}
          onClick={() => toast.info("Select text in editor, then click Merge Tags in toolbar")}
        >
          More...
        </Badge>
      </div>

      {/* Tabs for Editor/HTML */}
      <div style={{ flex: 1, position: "relative" }}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-0">
          <Tab eventKey="editor" title="Visual Editor">
            {/* Email Editor */}
            <div
              className="email-editor-wrapper"
              style={{
                position: "absolute",
                top: "42px",
                left: "0",
                right: "0",
                bottom: "0",
                border: "1px solid #ddd",
                backgroundColor: "#f9f9f9",
              }}
            >
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onEditorLoad}
                onReady={onEditorReady}
                options={editorOptions}
                minHeight="600px"
                style={{ height: "100%" }}
              />
            </div>
          </Tab>

          <Tab eventKey="html" title="HTML Source">
            <div className="p-3" style={{
              position: "absolute",
              top: "42px",
              left: "0",
              right: "0",
              bottom: "0",
              overflow: "auto"
            }}>
            <div className="d-flex justify-content-between mb-2">
              <small className="text-muted">Edit raw HTML (advanced)</small>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  exportHtml((data) => setHtmlSource(data.html));
                }}
              >
                Refresh from Editor
              </Button>
            </div>
            <textarea
              className="form-control font-monospace"
              rows={20}
              value={htmlSource}
              onChange={(e) => setHtmlSource(e.target.value)}
              style={{ fontSize: "12px" }}
              placeholder="HTML content will appear here after editing in the visual editor..."
            />
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* HTML Preview Modal */}
      {showHtmlSource && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}
          onClick={() => setShowHtmlSource(false)}
        >
          <div
            className="bg-white rounded shadow-lg p-4"
            style={{ maxWidth: "80%", maxHeight: "80%", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">HTML Export</h5>
              <Button variant="close" onClick={() => setShowHtmlSource(false)} />
            </div>
            <pre
              className="bg-light p-3 rounded"
              style={{ maxHeight: "400px", overflow: "auto", fontSize: "11px" }}
            >
              {htmlSource}
            </pre>
            <div className="mt-3 d-flex gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  navigator.clipboard.writeText(htmlSource);
                  toast.success("HTML copied to clipboard");
                }}
              >
                Copy HTML
              </Button>
              <Button variant="secondary" onClick={() => setShowHtmlSource(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && testEmailData && (
        <TestEmailModal
          show={showTestEmailModal}
          onHide={() => setShowTestEmailModal(false)}
          token={token}
          subject={testEmailData.subject}
          htmlBody={testEmailData.html_body}
          fromName={testEmailData.from_name}
          fromEmail={testEmailData.from_email}
          userEmail={userEmail}
        />
      )}

      {/* Image Library Modal */}
      {showImageLibrary && (
        <ImageLibrary
          show={showImageLibrary}
          onHide={() => {
            setShowImageLibrary(false);
            // Clear callback if modal closed without selection
            window._unlayerImageCallback = null;
          }}
          onSelectImage={handleSelectImageFromLibrary}
          token={token}
        />
      )}
    </div>
  );
};

export default EmailBuilder;
